import { addDays } from 'date-fns';
import { useSecondRecurringSessionCalendar } from 'domains/calendars/signup/useSecondRecurringSessionCalendar';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { getFilteringAreasByConcernKeys } from 'domains/signupDirect/misc/communicationConcerns';
import { useSplitEvaluationCtxV2 } from '../SplitEvaluationsScheduleSectionV2';
import { EvaluationDuration } from 'domains/calendars/types';
import { EvaluationCalendarSummary } from 'domains/calendars/v3/useEvaluationCalendarSummary';

export const useSignupSecondSplitCalendarDataV2 = () => {
  const { firstTimeSlot } = useSplitEvaluationCtxV2();
  const { filters } = useSignupDirectCtx();

  if (!firstTimeSlot) throw new Error('Expected first time slot to be set');
  if (!filters) throw new Error('Expected filters to be set');

  const { paymentMethod, stateCode } = filters;

  const isInsuranceAndInNetwork =
    paymentMethod.type === 'insurance' && paymentMethod.insuranceData.networkStatus === 'In-network';

  const fromDate = addDays(firstTimeSlot.time, 1);

  const query = useSecondRecurringSessionCalendar({
    firstSession: firstTimeSlot.time,
    fromDate,
    toDate: addDays(fromDate, 7),

    detectedAreas: getFilteringAreasByConcernKeys(filters.clinicalConcernKeys, filters.therapyTarget),
    insuranceName: isInsuranceAndInNetwork ? paymentMethod.insuranceData.insuranceName : undefined,
    state: stateCode,
    duration: EvaluationDuration.Split,
  });

  if (query.data) {
    return {
      ...query,
      data: filterDataByTherapistIDs(
        query.data,
        firstTimeSlot.therapists.map(t => t.therapistID),
      ),
    };
  }

  return query;
};

/** @todo(juan): temporal solution until we refactor the entire tree of availability hooks */
const filterDataByTherapistIDs = (
  data: EvaluationCalendarSummary,
  therapistIDs?: string[],
): EvaluationCalendarSummary => {
  if (!therapistIDs) return data;

  const filteredSummary = data.summaryData.map(dayData => {
    const availabilityBySlot = dayData.availabilityBySlot.filter(slot =>
      slot.therapists.some(t => therapistIDs.includes(t.therapistID)),
    );

    const timeSlotCount = availabilityBySlot.length;

    return {
      ...dayData,
      availabilityBySlot,
      timeSlotCount,
    };
  });

  return {
    ...data,
    totalTimeSlots: filteredSummary.reduce((acc, dayData) => acc + dayData.timeSlotCount, 0),
    summaryData: filteredSummary,
  };
};
